import type {
  RoleplayAttempt,
  RoleplayAttemptsResponse,
  RoleplayCard,
  RoleplayDeleteImpactResponse,
  RoleplayDetail,
  RoleplayListResponse,
  StartRoleplayResponse,
} from '@wundo/shared/roleplay.ts';
import { apiFetch, apiFetchJson } from '@wundo/client/api.ts';

export type {
  RoleplayAttempt,
  RoleplayCard,
  RoleplayDeleteImpactResponse,
  RoleplayDetail,
  RoleplayListResponse,
};

/** Everything an admin writes on the authoring page, in the shape the API takes. */
export interface RoleplayInput {
  title: string;
  /** The type label, or null when the author left it blank. */
  type_label: string | null;
  /** The track or the cohort it is for, never both. Both null is the whole organisation. */
  track_id: string | null;
  cohort_id: string | null;
  scenario: {
    situation: string;
    persona: {
      name: string;
      role: string;
      behavior: string;
      /** Empty lets the persona open with a line of its own. */
      opening_line: string;
    };
    learning_objectives: string[];
    /** Background for the model. Empty when there is none. */
    extra_instructions: string;
    /** Rules for the feedback afterwards; the character never reads them. Empty when there are none. */
    feedback_instructions: string;
  };
}

/**
 * Everything the Oefenen page shows: the organisation's library and the user's
 * own practice conversations, in one request.
 *
 * Both tabs come from the same call because they are one page — asking twice
 * would let the two halves disagree about how often something was practised,
 * and the counts are per user, not per list. Drafts are in the library only
 * when the caller may manage it, which the same answer says.
 */
export async function fetchRoleplays(): Promise<RoleplayListResponse> {
  return apiFetchJson<RoleplayListResponse>('/roleplays');
}

/**
 * The caller's own attempts at one practice conversation, newest first.
 *
 * Read when the feedback is opened rather than with the list: most cards are
 * never opened, and an attempt carries a whole debrief.
 */
export async function fetchRoleplayAttempts(
  roleplayId: string,
): Promise<RoleplayAttempt[]> {
  const { data } = await apiFetchJson<RoleplayAttemptsResponse>(
    `/roleplays/${roleplayId}/attempts`,
  );
  return data;
}

/** Start a new attempt. The session it hands back is where the practice happens. */
export async function startRoleplaySession(
  roleplayId: string,
): Promise<StartRoleplayResponse> {
  return apiFetchJson<StartRoleplayResponse>(`/roleplays/${roleplayId}/sessions`, {
    method: 'POST',
  });
}

/**
 * One library roleplay as its author edits it.
 *
 * Not read from the list: the list carries cards, and a card deliberately
 * leaves out the extra context, which is written here and never shown to the
 * people practising. The API refuses this to anybody who may not edit.
 */
export async function fetchRoleplayDetail(roleplayId: string): Promise<RoleplayDetail> {
  return apiFetchJson<RoleplayDetail>(`/roleplays/${roleplayId}`);
}

/** Write a new one. It starts as a draft; publishing is a second step. */
export async function createRoleplay(input: RoleplayInput): Promise<RoleplayCard> {
  return apiFetchJson<RoleplayCard>('/roleplays', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function updateRoleplay(
  roleplayId: string,
  input: RoleplayInput,
): Promise<RoleplayCard> {
  return apiFetchJson<RoleplayCard>(`/roleplays/${roleplayId}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

/** Hand it to the organisation. From here on everybody sees it. */
export async function publishRoleplay(roleplayId: string): Promise<RoleplayCard> {
  return apiFetchJson<RoleplayCard>(`/roleplays/${roleplayId}/actions/publish`, {
    method: 'POST',
  });
}

/**
 * The order the library is read in, as the whole list rather than as a move.
 *
 * Sending "this one goes up" would leave the server applying it to a list the
 * author was not looking at; sending the list they *are* looking at is the
 * order they meant. The API refuses ids that are not the organisation's own
 * library, so a stale page fails loudly instead of half-arranging it.
 */
export async function reorderRoleplays(ids: string[]): Promise<void> {
  await apiFetch('/roleplays/actions/reorder', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });
}

/**
 * Throw a practice conversation away, with every attempt made at it.
 *
 * Published ones go too. What that costs is not guessed here — the confirm
 * asks the API first, so the numbers people are shown are the ones the delete
 * will actually act on.
 */
export async function deleteRoleplay(roleplayId: string): Promise<void> {
  await apiFetch(`/roleplays/${roleplayId}`, { method: 'DELETE' });
}

/**
 * How much practice deleting this conversation would take with it.
 *
 * Read when the confirm opens rather than with the list: it counts the whole
 * organisation's sessions, which is a question worth asking only about the one
 * scenario somebody is on the point of deleting.
 */
export async function fetchRoleplayDeleteImpact(
  roleplayId: string,
): Promise<RoleplayDeleteImpactResponse> {
  return apiFetchJson<RoleplayDeleteImpactResponse>(
    `/roleplays/${roleplayId}/delete-impact`,
  );
}
